// User Service for MUU Platform API
import bcryptjs from "bcryptjs";
import { apiClient, handleApiResponse } from "./api";
import { apiClientBff } from "./apiBff";
import { API_ENDPOINTS, API_CONFIG } from "../config/api";
import { LoginRequest, AuthUser } from "../types/auth";
import {
  User,
  Role,
  ApiResponse,
  SearchRequest,
  InsertUserRequest,
  UpdateUserRequest,
  DeleteUserRequest,
  CreateUserData,
  LoginCredentials,
} from "../types/user";

export class UserService {
  private readonly endpoint = "/Users";
  private readonly rolesEndpoint = "/Roles";
  private readonly userAccountsEndpoint = "/UserAccounts";

  /**
   * Hash password using bcrypt
   */
  private async hashPassword(password: string): Promise<string> {
    const saltRounds = 12;
    return await bcryptjs.hash(password, saltRounds);
  }

  /**
   * Decode JWT token to extract payload
   */
  private decodeJWTToken(token: string): any {
    try {
      // JWT tokens have 3 parts separated by dots: header.payload.signature
      const parts = token.split(".");
      if (parts.length !== 3) {
        throw new Error("Invalid JWT token format");
      }

      // Decode the payload (second part)
      const payload = parts[1];
      // Add padding if needed for base64 decoding
      const paddedPayload =
        payload + "=".repeat((4 - (payload.length % 4)) % 4);
      const decodedPayload = atob(
        paddedPayload.replace(/-/g, "+").replace(/_/g, "/")
      );

      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error("Error decoding JWT token:", error);
      return null;
    }
  }

  /**
   * Search for users with filters and pagination
   * POST /api/v2/Users/search
   */
  async searchUsers(searchRequest: SearchRequest = {}): Promise<{
    users: User[];
    meta?: {
      count: number;
      total: number;
      skip: number;
      limit: number;
    };
  }> {
    try {
      const response = await apiClient.post<ApiResponse<User[]>>(
        `${this.endpoint}/search`,
        searchRequest
      );

      return {
        users: response.data,
        meta: response.meta,
      };
    } catch (error) {
      console.error("Error searching users:", error);
      throw error;
    }
  }

  /**
   * Find user by email
   */
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const result = await this.searchUsers({
        filters: { email },
        options: { limit: 1 },
      });

      return result.users.length > 0 ? result.users[0] : null;
    } catch (error) {
      console.error("Error getting user by email:", error);
      throw error;
    }
  }

  /**
   * Find user by phone
   */
  async getUserByPhone(phone: string): Promise<User | null> {
    try {
      const result = await this.searchUsers({
        filters: { phone },
        options: { limit: 1 },
      });

      return result.users.length > 0 ? result.users[0] : null;
    } catch (error) {
      console.error("Error getting user by phone:", error);
      throw error;
    }
  }

  /**
   * Find user by username
   */
  async getUserByUsername(username: string): Promise<User | null> {
    try {
      const result = await this.searchUsers({
        filters: { username },
        options: { limit: 1 },
      });

      return result.users.length > 0 ? result.users[0] : null;
    } catch (error) {
      console.error("Error getting user by username:", error);
      throw error;
    }
  }

  /**
   * Create a new user
   * POST /auth/users
   * Requires admin authentication and HTTPS
   */
  async createUser(userData: CreateUserData): Promise<User> {
    // Logs de debug removidos

    try {
      // Prepare user payload according to working curl example
      const userPayload = {
        username: userData.username || userData.email,
        email: userData.email,
        password: userData.password,
        roles: [userData.role || "general_user"],
        is_active: userData.isActive !== false,
      };

      // console.log('🚀 Sending user creation payload:', JSON.stringify(userPayload, null, 2));
      // console.log('📝 Original user data received:', JSON.stringify(userData, null, 2));
      
      // Logs removidos: nenhum token de teste é utilizado no frontend

      const response = await apiClient.authPost<any>(
        "/auth/users",
        userPayload
      );

      // Convert response to User format
      const user: User = {
        phone: userData.phone,
        email: userData.email,
        full_name: userData.full_name,
        role: userData.role || "general_user",
        farm_role_assignments: [],
        language_preference: userData.language_preference || "pt",
        timezone: userData.timezone || "America/Sao_Paulo",
        lastLogin: new Date().toISOString(),
        isActive: userPayload.is_active,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return user;
    } catch (error) {
      console.error("❌ Error creating user:", error);
      console.error("❌ Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        stack: error instanceof Error ? error.stack : "No stack trace",
        userData: userData,
      });
      throw error;
    }
  }

  /**
   * Update user information
   * POST /api/v2/Users/update
   */
  async updateUser(username: string, updateData: Partial<User>): Promise<void> {
    try {
      const request: UpdateUserRequest = {
        filters: { username: username },
        update: {
          ...updateData,
          updatedAt: new Date().toISOString(),
        },
      };

      await apiClient.post<ApiResponse<any>>(
        `${this.endpoint}/update`,
        request
      );
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  }

  /**
   * Update user's last login timestamp
   */
  async updateLastLogin(userId: string): Promise<void> {
    try {
      await this.updateUser(userId, {
        lastLogin: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error updating last login:", error);
      throw error;
    }
  }

  /**
   * Activate or deactivate user
   */
  async setUserActiveStatus(userId: string, isActive: boolean): Promise<void> {
    try {
      await this.updateUser(userId, { isActive: isActive });
    } catch (error) {
      console.error("Error updating user active status:", error);
      throw error;
    }
  }

  /**
   * Delete user
   * POST /api/v2/Users/delete
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      const request: DeleteUserRequest = {
        filters: { _key: userId },
      };

      await apiClient.post<ApiResponse<any>>(
        `${this.endpoint}/delete`,
        request
      );
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  }

  /**
   * Authenticate user - searches by phone or email
   */
  async authenticateUser(credentials: LoginCredentials): Promise<User | null> {
    try {
      let user: User | null = null;

      if (credentials.phone) {
        user = await this.getUserByPhone(credentials.phone);
      } else if (credentials.email) {
        user = await this.getUserByEmail(credentials.email);
      }

      if (user && user.isActive) {
        // Update last login
        await this.updateLastLogin(user._key!);
        return user;
      }

      return null;
    } catch (error) {
      console.error("Error authenticating user:", error);
      throw error;
    }
  }

  /**
   * Check if user exists by email or phone
   */
  async userExists(emailOrPhone: string): Promise<boolean> {
    try {
      // Check if it's an email or phone number
      const isEmail = emailOrPhone.includes("@");

      const user = isEmail
        ? await this.getUserByEmail(emailOrPhone)
        : await this.getUserByPhone(emailOrPhone);

      return user !== null;
    } catch (error) {
      console.error("Error checking if user exists:", error);
      return false;
    }
  }

  /**
   * Authenticate user with password
   * POST /api/v2/UserAccounts/authenticate
   */
  async authenticateUserWithPassword(
    credentials: LoginCredentials
  ): Promise<User | null> {
    try {
      const authPayload: Record<string, string> = {};

      if (credentials.email) {
        authPayload.email = credentials.email;
      }
      if (credentials.phone) {
        authPayload.phone = credentials.phone;
      }
      if (credentials.password) {
        authPayload.password = credentials.password;
      }

      const response = await apiClient.post<ApiResponse<User>>(
        `${this.userAccountsEndpoint}/authenticate`,
        authPayload
      );

      return handleApiResponse<User>(response);
    } catch (error) {
      console.error("Error authenticating user with password:", error);
      return null;
    }
  }

  // ===== AUTHENTICATION METHODS =====

  /**
   * Authenticate user with JWT tokens
   * POST /auth/login
   */
  async authenticateWithJWT(credentials: LoginRequest): Promise<{
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }> {
    try {
      // Determine identifier: prefer email for new API
      const identifier = credentials.email || credentials.username || credentials.phone || "";

      // Call new login endpoint with JSON body via authPost (skip auth, no /api/v2)
      const loginResponse = await apiClientBff.authPost<{
        message?: string;
        user?: any;
        token?: string;
        accessToken?: string;
        access_token?: string;
      }>(API_ENDPOINTS.AUTH.LOGIN, {
        email: identifier,
        password: credentials.password,
      });

      const accessToken = (loginResponse as any).token ?? (loginResponse as any).accessToken ?? (loginResponse as any).access_token;
      if (!accessToken) {
        throw new Error("Login failed: missing token");
      }

      // Extract user information from JWT token payload
      let user: AuthUser;

      try {
        // Decode the JWT token to get user information
        const tokenPayload = this.decodeJWTToken(accessToken);

        if (tokenPayload && tokenPayload.sub) {
          // Use information from the JWT token
          user = {
            id: tokenPayload.sub,
            email: tokenPayload.email || identifier,
            username: tokenPayload.username || identifier,
            full_name: tokenPayload.name || tokenPayload.full_name || (identifier || '').split('@')[0] || identifier,
            role: tokenPayload.role || "user",
            phone: tokenPayload.phone || "",
            language_preference: tokenPayload.language_preference || "pt",
            timezone: tokenPayload.timezone || "America/Sao_Paulo",
            is_active: tokenPayload.is_active !== false,
            last_login: new Date().toISOString(),
            farm_role_assignments: tokenPayload.farm_role_assignments || [],
          };
        } else {
          // Fallback to minimal user object if token doesn't contain user info
          user = {
            id: "",
            email: identifier,
            username: identifier,
            full_name: (identifier || '').split('@')[0] || identifier,
            role: "user",
            phone: "",
            language_preference: "pt",
            timezone: "America/Sao_Paulo",
            is_active: true,
            last_login: new Date().toISOString(),
            farm_role_assignments: [],
          };
        }
      } catch (tokenError) {
        // console.warn(
        //   "⚠️ Could not decode JWT token, using minimal user object:",
        //   tokenError
        // );
        user = {
          id: "",
          email: identifier,
          username: identifier,
          full_name: (identifier || '').split('@')[0] || identifier,
          role: "user",
          phone: "",
          language_preference: "pt",
          timezone: "America/Sao_Paulo",
          is_active: true,
          last_login: new Date().toISOString(),
          farm_role_assignments: [],
        };
      }

      // Compute expiresIn from JWT payload if available
      const payload = this.decodeJWTToken(accessToken);
      const nowSec = Math.floor(Date.now() / 1000);
      const expiresIn = payload?.exp ? Math.max(payload.exp - nowSec, 0) : 3600;

      // Map returned user object if present in API response
      const apiUser = loginResponse.user;
      if (apiUser) {
        user = {
          id: apiUser.userId || apiUser._key || apiUser.id || user.id,
          email: apiUser.email || user.email,
          username: apiUser.username || user.username,
          full_name: user.full_name || apiUser.username || (apiUser.email ? apiUser.email.split('@')[0] : user.full_name),
          role: (Array.isArray(apiUser.roles) && apiUser.roles[0]) ? apiUser.roles[0] : user.role,
          phone: user.phone,
          language_preference: user.language_preference,
          timezone: user.timezone,
          is_active: apiUser.is_active !== false && apiUser.status !== 'inactive',
          last_login: apiUser.last_login || user.last_login,
          farm_role_assignments: user.farm_role_assignments || [],
        };
      }

      return {
        user,
        accessToken,
        refreshToken: accessToken, // API não fornece refresh token; usar o mesmo para compatibilidade
        expiresIn,
      };
    } catch (error) {
      console.error("❌ JWT authentication failed:", error);
      throw error;
    }
  }

  /**
   * Refresh JWT tokens
   * POST /api/v2/auth/refresh
   */
  async refreshJWTTokens(refreshToken: string): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  }> {
    try {
      // Prefer BFF for refresh; upstream returns only { token }
      const response = await apiClientBff.post(
        API_ENDPOINTS.AUTH.REFRESH,
        undefined,
        { skipAuth: true, headers: { Authorization: `Bearer ${refreshToken}` } }
      );

      const token = (response as any).token || (response as any).accessToken || (response as any).access_token;
      if (!token) throw new Error('Refresh failed: missing token');

      const payload = this.decodeJWTToken(token);
      const nowSec = Math.floor(Date.now() / 1000);
      const expiresIn = payload?.exp ? Math.max(payload.exp - nowSec, 0) : 3600;

      return {
        accessToken: token,
        refreshToken: token,
        expiresIn,
      };
    } catch (error) {
      console.error("Token refresh failed:", error);
      throw error;
    }
  }

  /**
   * Logout user and invalidate tokens
   * POST /api/v2/auth/logout
   */
  async logoutUser(refreshToken?: string): Promise<void> {
    try {
      await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT, { refreshToken });
    } catch (error) {
      console.error("Logout failed:", error);
      // Don't throw error for logout - always clear local tokens
    }
  }

  /**
   * Verify JWT token
   * POST /api/v2/auth/verify
   */
  async verifyToken(
    token: string
  ): Promise<{ valid: boolean; user?: AuthUser }> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.AUTH.VERIFY,
        { token },
        { skipAuth: true }
      );

      return response as { valid: boolean; user?: AuthUser };
    } catch (error) {
      console.error("Token verification failed:", error);
      return { valid: false };
    }
  }

  // ===== ROLE MANAGEMENT =====

  /**
   * Get all available roles
   * POST /api/v2/Roles/search
   */
  async getRoles(): Promise<Role[]> {
    try {
      const response = await apiClient.post<ApiResponse<Role[]>>(
        `${this.rolesEndpoint}/search`,
        {
          filters: {},
          options: { limit: 200, sort: 'name asc' },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching roles:", error);
      throw error;
    }
  }

  /**
   * Get role by key
   */
  async getRoleByKey(roleKey: string): Promise<Role | null> {
    try {
      const response = await apiClient.post<ApiResponse<Role[]>>(
        `${this.rolesEndpoint}/search`,
        {
          filters: { _key: roleKey },
          options: { limit: 1 },
        }
      );

      return response.data.length > 0 ? response.data[0] : null;
    } catch (error) {
      console.error("Error getting role by key:", error);
      throw error;
    }
  }

  /**
   * Update user's farm role assignments
   */
  async updateUserFarmRoles(
    userId: string,
    farmRoleAssignments: Array<{ farm_id: string; role_keys: string[] }>
  ): Promise<void> {
    try {
      await this.updateUser(userId, {
        farm_role_assignments: farmRoleAssignments,
      });
    } catch (error) {
      console.error("Error updating user farm roles:", error);
      throw error;
    }
  }
}

// Create singleton instance
export const userService = new UserService();

// Export default
export default userService;
